<script setup lang="ts">
import { Button, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"

interface PaginationProps {
  variant?: "default" | "simple"
  currentPage?: number
  pageCount?: number
  perPage?: number
  total?: number
  canNext?: boolean
  canPrevious?: boolean
  isFetching?: boolean
}

const props = withDefaults(defineProps<PaginationProps>(), {
  variant: "default",
  currentPage: 1,
  pageCount: 1,
  canNext: false,
  canPrevious: false,
  isFetching: false,
})

const emit = defineEmits<{
  previous: [current: number]
  next: [current: number]
}>()

const { t } = useTranslation()

const onNext = () => emit("next", props.currentPage)
const onPrev = () => emit("previous", props.currentPage)
</script>

<template>
  <div :class="`gpx-pagination gpx-pagination_${variant} flex justify-between items-center`">
    <div
      v-if="variant !== 'simple' && perPage && total"
      class="hidden md:block font-montserrat font-medium text-[#333333]"
    >
      {{ t("pagination.showing", { perPage, total }) }}
    </div>
    <div v-if="variant === 'default'" class="gpx-pagination-marker font-montserrat font-medium text-[#333333] mr-3">
      <span class="md:hidden">
        {{ t("pagination.count_simple", { currentPage, pageCount }) }}
      </span>
      <span class="hidden md:inline">
        {{ t("pagination.count", { currentPage, pageCount }) }}
      </span>
    </div>
    <div class="flex items-center gap-1.5 md:gap-3">
      <component
        :is="variant === 'simple' ? 'button' : Button"
        :variant="variant === 'default' ? 'icon' : 'tertiary'"
        :disabled="!canPrevious"
        data-test="gpx-pagination-previous"
        @click="onPrev"
      >
        <Icon v-if="variant === 'simple'" icon="CirclePrevious" />
        <span :class="{ 'sr-only': variant === 'simple' }">{{ t("pagination.previous") }}</span>
      </component>
      <div v-if="variant === 'simple'" class="gpx-pagination-marker">
        {{ t("pagination.count_simple", { currentPage, pageCount }) }}
      </div>
      <component
        :is="variant === 'simple' ? 'button' : Button"
        :variant="variant === 'default' ? 'icon' : 'tertiary'"
        :disabled="!canNext"
        data-test="gpx-pagination-next"
        @click="onNext"
      >
        <Icon v-if="variant === 'simple' && !isFetching" icon="CircleNext" />
        <Icon v-if="isFetching" icon="Loading" />
        <span :class="{ 'sr-only': variant === 'simple' }">{{ t("pagination.next") }}</span>
      </component>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@reference "../../../assets/styles.css";

.gpx-pagination-marker {
  white-space: nowrap;
}

.gpx-pagination_simple .gpx-pagination-marker {
  margin: 0 0.5rem;

  @apply text-sm font-normal text-white;
}

.gpx-pagination_simple button {
  @apply text-white;
}

.gpx-pagination_simple button:hover {
  @apply text-secondary;
}

.gpx-pagination_simple button:disabled {
  @apply text-gray-300;
}
</style>
